import React, { Component } from 'react'
import { withApollo } from 'react-apollo'
import gql from 'graphql-tag'
import { Card, CardHeader, CardBody, Table, Input, FormGroup, Label, Col, Row } from 'reactstrap'
import { RiseLoader } from 'react-spinners'
import { store } from '../../stores/reduxStore'

const PROJECTS_QUERY = gql`
  query WeldLeadTimeProjects {
    projects {
      list { id name project_companies }
    }
  }
`

const PERSON_QUERY = gql`
  query WeldLeadTimePerson($person_id: ID!) {
    person(id: $person_id) { company_ids }
  }
`

const DATA_QUERY = gql`
  query StatsWeldLeadTime($project_id: ID!) {
    stats_weld_lead_time(project_id: $project_id) {
      project_id project_name
      by_element {
        element depth welds_total welds_ok
        avg_days_ready_to_welding avg_days_ready_to_ok avg_days_welding_to_ok
      }
    }
  }
`

const dayColor = (days) => {
  if (!days || days === 0) return 'inherit'
  if (days <= 7) return '#28a745'
  if (days <= 30) return '#ffc107'
  return '#dc3545'
}

class WeldLeadTime extends Component {
  constructor(props) {
    super(props)
    const params = new URLSearchParams(window.location.search)
    this.state = {
      projects: [],
      project_id: params.get('project_id') || '',
      data: null,
      loading: false,
      error: null,
      sortBy: 'element', sortDir: 'asc', filter: ''
    }
  }

  componentDidMount() {
    const { client } = this.props
    const personId = store.getState().user.data.person_id
    Promise.all([
      client.query({ query: PROJECTS_QUERY, fetchPolicy: 'network-only' }),
      client.query({ query: PERSON_QUERY, variables: { person_id: personId }, fetchPolicy: 'network-only' })
    ]).then(([pRes, meRes]) => {
      const userCompanyIds = (meRes.data.person.company_ids || []).map(String)
      const allProjects = pRes.data.projects.list
      const projects = allProjects.filter(p => {
        if (!userCompanyIds.length) return true
        const pc = typeof p.project_companies === 'string' ? JSON.parse(p.project_companies) : (p.project_companies || [])
        return pc.some(c => userCompanyIds.includes(String(c.id)))
      })
      this.setState({ projects }, () => {
        if (this.state.project_id) this.loadData(this.state.project_id)
      })
    })
  }

  loadData(project_id) {
    this.setState({ loading: true, error: null })
    this.props.client.query({
      query: DATA_QUERY,
      variables: { project_id },
      fetchPolicy: 'network-only'
    }).then(({ data }) => {
      this.setState({ data: data.stats_weld_lead_time[0] || null, loading: false })
    }).catch(e => {
      this.setState({ error: e.message, loading: false })
    })
  }

  handleProjectChange(project_id) {
    this.setState({ project_id, data: null })
    const url = project_id
      ? `${window.location.pathname}?project_id=${project_id}`
      : window.location.pathname
    window.history.replaceState(null, '', url)
    if (project_id) this.loadData(project_id)
  }

  toggleSort(col) {
    this.setState(s => ({ sortBy: col, sortDir: s.sortBy === col && s.sortDir === 'asc' ? 'desc' : 'asc' }))
  }

  render() {
    const { projects, project_id, data, loading, error, sortBy, sortDir, filter } = this.state

    const rows = data ? [...data.by_element]
      .filter(e => !filter || e.element.toLowerCase().includes(filter.toLowerCase()))
      .sort((a, b) => {
        if (sortBy === 'element' && sortDir === 'asc') return 0 // preserve tree order
        const av = a[sortBy], bv = b[sortBy]
        if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av)
        return sortDir === 'asc' ? (av || 0) - (bv || 0) : (bv || 0) - (av || 0)
      }) : []

    const th = (col, label) => (
      <th key={col} className="text-center" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => this.toggleSort(col)}>
        {label} {sortBy === col ? (sortDir === 'desc' ? '▼' : '▲') : '⇅'}
      </th>
    )

    return (
      <div className="animated fadeIn">
        <Card>
          <CardHeader><strong><i className="fa fa-clock-o mr-2" />Weld Lead Time</strong></CardHeader>
          <CardBody style={{ position: 'relative', minHeight: '200px' }}>
            <Row className="mb-3">
              <Col sm={4}>
                <FormGroup>
                  <Label>Project</Label>
                  <Input type="select" value={project_id} onChange={e => this.handleProjectChange(e.target.value)}>
                    <option value="">— select project —</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </Input>
                </FormGroup>
              </Col>
              {data && (
                <Col sm={4}>
                  <FormGroup>
                    <Label>Filter element</Label>
                    <Input type="text" placeholder="Search..." value={filter}
                      onChange={e => this.setState({ filter: e.target.value })} />
                  </FormGroup>
                </Col>
              )}
            </Row>

            {loading && <div className="spinner-item" id="loader-bg"><RiseLoader color="#20a8d8" loading={true} /></div>}
            {error && <div className="text-danger">Error: {error}</div>}
            {!project_id && !loading && <div className="text-muted">Select a project to view data.</div>}

            {data && (
              <div>
                <div style={{ overflowX: 'auto' }}>
                  <Table bordered striped size="sm">
                    <thead>
                      <tr>
                        {th('element', 'Element')}
                        {th('welds_total', 'Total welds')}
                        {th('welds_ok', 'OK')}
                        {th('avg_days_ready_to_welding', 'AVG welding (days)')}
                        {th('avg_days_welding_to_ok', 'AVG inspection (days)')}
                        {th('avg_days_ready_to_ok', 'AVG complete (days)')}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(e => (
                        <tr key={e.element}>
                          <td style={{ paddingLeft: `${(e.depth - 2) * 16 + 8}px` }}><strong>{e.element}</strong></td>
                          <td className="text-center">{e.welds_total}</td>
                          <td className="text-center">{e.welds_ok}</td>
                          <td className="text-center" style={{ color: dayColor(e.avg_days_ready_to_welding) }}>{e.avg_days_ready_to_welding || '—'}</td>
                          <td className="text-center" style={{ color: dayColor(e.avg_days_welding_to_ok) }}>{e.avg_days_welding_to_ok || '—'}</td>
                          <td className="text-center" style={{ color: dayColor(e.avg_days_ready_to_ok) }}><strong>{e.avg_days_ready_to_ok || '—'}</strong></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                <div className="mt-3 p-3 border rounded bg-light" style={{ fontSize: '12px' }}>
                  <strong>How it's calculated:</strong>
                  <ul className="mb-0 mt-1 pl-3">
                    <li><strong>AVG welding</strong> — avg days from WPS assignment (<code>status → ready</code>) to welding completion (<code>status → in_progress</code>) per weld</li>
                    <li><strong>AVG inspection</strong> — avg days from welding completion to all inspections done (<code>status → ok</code>) per weld</li>
                    <li><strong>AVG complete</strong> — avg total lead time from WPS assignment (<code>status → ready</code>) to all inspections done (<code>status → ok</code>) per weld</li>
                    <li>Color: <span style={{ color: '#28a745' }}>green ≤7 days</span>, <span style={{ color: '#ffc107' }}>yellow ≤30 days</span>, <span style={{ color: '#dc3545' }}>red &gt;30 days</span></li>
                  </ul>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    )
  }
}

export default withApollo(WeldLeadTime)



// WEBPACK FOOTER //
// ./src/components/Report/WeldLeadTime.js